/** One conjugation / declension table.
 *
 * Every table in the library renders through this component, which is why
 * GrammarTable is a generic columns/rows shape rather than a schema per
 * table type — a new table is content, never code.
 *
 * Two deliberate choices:
 *  - the first column is treated as a row header (Kasus, Person, Zeitform),
 *    because that is what makes a declension table readable at a glance;
 *  - `highlight` marks the cells that actually CHANGE, which is the whole
 *    lesson of a declension table and is otherwise lost in a wall of words.
 */
import type { GrammarTable } from "../../api/reference";
import { TapToTranslate } from "../../components/TapToTranslate";

export function GrammarTableView({ table }: { table: GrammarTable }) {
  const isHighlighted = (r: number, c: number) =>
    table.highlight?.some(([hr, hc]) => hr === r && hc === c);

  return (
    <div className="rounded-card border border-line bg-surface p-4">
      <h3 className="font-bold text-ink">
        <TapToTranslate text={table.title} />
      </h3>
      {table.description && (
        <div className="mt-1 text-sm text-muted">
          <TapToTranslate block text={table.description} />
        </div>
      )}

      {/* wide tables scroll inside their own box; the page never scrolls sideways */}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-max border-collapse text-sm">
          <thead>
            <tr>
              {table.columns.map((c) => (
                <th
                  key={c}
                  scope="col"
                  className="border-b border-line px-3 py-2 text-left font-semibold text-muted"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, r) => (
              <tr key={r} className="border-b border-line/60 last:border-0">
                {row.map((cell, c) =>
                  c === 0 ? (
                    <th
                      key={c}
                      scope="row"
                      className="px-3 py-2 text-left font-semibold text-ink"
                    >
                      {cell}
                    </th>
                  ) : (
                    <td
                      key={c}
                      className={`px-3 py-2 ${
                        isHighlighted(r, c)
                          ? "bg-brand-soft font-bold text-brand-dark"
                          : "text-ink"
                      }`}
                    >
                      {cell}
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {table.notes && (
        <div className="mt-3 text-xs text-muted">
          <TapToTranslate block text={table.notes} />
        </div>
      )}
    </div>
  );
}
